import React, { useState } from 'react';
import { View, Text, TouchableOpacity, Platform } from 'react-native';
import PlatformIcon from '../common/PlatformIcon';

interface OpeningHoursRowProps {
  label: string;
  // accept flexible shapes: string, string[], or object maps from day keys to strings
  value: any;
  /** Optional date string (YYYY-MM-DD) or Date object to determine "today" */
  targetDate?: string | Date;
}

const OpeningHoursRow: React.FC<OpeningHoursRowProps> = ({ label, value, targetDate }) => {
  const [expanded, setExpanded] = useState(false);

  // Build normalized lines array based on value type (handle object/array/string)
  let lines: string[] = [];
  try {
    if (value && typeof value === 'object' && !Array.isArray(value)) {
      const map = value as Record<string, any>;
      const dayOrder = ['mon','tue','wed','thu','fri','sat','sun'];
      const dayName: Record<string,string> = { mon:'周一', tue:'周二', wed:'周三', thu:'周四', fri:'周五', sat:'周六', sun:'周日' };
      const values = dayOrder.map(d => map[d]).filter(v => typeof v !== 'undefined');
      if (values.length) {
        const allSame = values.every(v => v === values[0]);
        if (allSame) {
          const v = String(values[0] ?? '').trim();
          const line = `周一-周日 ${v || '营业'}`;
          lines = [line];
        } else {
          lines = dayOrder
            .filter(d => typeof map[d] !== 'undefined')
            .map(d => `${dayName[d]} ${String(map[d])}`);
        }
      }
      // fallback: if map had other keys (e.g., 'Monday', 'monday'), try to include them
      if (lines.length === 0) {
        const objEntries = Object.entries(map).filter(([k,v]) => typeof v !== 'undefined' && v !== null);
        if (objEntries.length === 1) {
          const [k, v] = objEntries[0];
          const raw = String(v || '').trim();
          // Drop common 'raw' prefix coming from some providers
          const cleaned = raw.replace(/^\s*raw[:\s-]*/i, '').trim();

          // If the single value contains multiple day segments, split before day tokens
          const daySplit = cleaned.split(/(?=(?:周|週|星期|Mon|Tue|Wed|Thu|Fri|Sat|Sun))/i).map(s => s.trim()).filter(Boolean);
          if (daySplit.length > 1) {
            lines = daySplit;
          } else {
            // fallback to common delimiters (newline, semicolon, pipe, slash)
            const parts = cleaned.split(/[\n;；|\/]+/).map(s => s.trim()).filter(Boolean);
            if (parts.length > 1) lines = parts;
            else lines = cleaned ? [cleaned] : [];
          }
        } else {
          const entries = objEntries.map(([k,v]) => `${String(k)} ${String(v)}`).filter(s => s && s.trim());
          if (entries.length) lines = entries;
        }
      }
    } else if (Array.isArray(value)) {
      lines = value.map(v => String(v || '').trim()).filter(Boolean);
    } else if (typeof value === 'string') {
      lines = value.split('\n').map(s => s.trim()).filter(Boolean);
    } else if (value != null) {
      // fallback for primitives like numbers
      lines = [String(value)];
    }
  } catch (e) {
    lines = [String(value || '')];
  }

  // Helper to find today's line
  const getTodayLine = () => {
    let day: number;
    if (targetDate) {
      const d = new Date(targetDate);
      if (!isNaN(d.getTime())) {
        day = d.getDay();
      } else {
        day = new Date().getDay();
      }
    } else {
      day = new Date().getDay(); // 0 = Sun, 1 = Mon...
    }
    
    const dayMap: Record<number, string[]> = {
      0: ['Sun', '週日', '周日', '星期日', '星期天'],
      1: ['Mon', '週一', '周一', '星期一'],
      2: ['Tue', '週二', '周二', '星期二'],
      3: ['Wed', '週三', '周三', '星期三'],
      4: ['Thu', '週四', '周四', '星期四'],
      5: ['Fri', '週五', '周五', '星期五'],
      6: ['Sat', '週六', '周六', '星期六'],
    };
    const keywords = dayMap[day] || [];
    
    // Find line starting with or containing the keyword
    // Priority: Starts with keyword > Contains keyword
    const match = lines.find(line => keywords.some(k => line.toLowerCase().startsWith(k.toLowerCase()))) 
               || lines.find(line => keywords.some(k => line.includes(k)));
               
    return match || lines[0] || '暫無今日資訊';
  };

  const todayLine = getTodayLine();

  const hasLabel = !!(label && String(label).trim());

  const containerStyle: any = hasLabel
    ? { paddingVertical: 8, borderBottomWidth: 1, borderBottomColor: '#F3F4F6', flexDirection: 'row', alignItems: 'center', position: 'relative' }
    : { paddingVertical: 0, borderBottomWidth: 0, flexDirection: 'row', alignItems: 'center', position: 'relative' };

  return (
    <View style={containerStyle}>
      {/* Label 30% (hidden if empty) */}
      {hasLabel ? (
        <Text style={{ width: '30%', color: '#6B7280', fontSize: 14, alignSelf: 'center' }}>{label}</Text>
      ) : null}
      
      {/* Content (take remaining space or full width when no label) */}
      <View style={{ flex: 1, flexDirection: 'row', alignItems: 'flex-start', justifyContent: 'flex-start' }}>
        <TouchableOpacity 
          activeOpacity={0.7}
          onPress={() => setExpanded(!expanded)}
          style={{ flex: 1, paddingRight: 40 }}
        >
          {expanded ? (
            <View>
              {lines.map((line, idx) => {
                const isToday = line === todayLine;
                return (
                  <Text
                    key={idx}
                    style={{
                      color: isToday ? '#111827' : '#6B7280',
                      fontSize: 13,
                      lineHeight: 20,
                      marginBottom: 2,
                      fontWeight: isToday ? '700' : '400'
                    }}
                  >
                    {line}
                  </Text>
                );
              })}
            </View>
          ) : (
            <Text 
              style={{ color: '#111827', fontSize: 14, fontWeight: '700' }}
              numberOfLines={1}
            >
              {todayLine}
            </Text>
          )}
        </TouchableOpacity>
        <TouchableOpacity
          activeOpacity={0.7}
          onPress={() => setExpanded(!expanded)}
          style={{ 
            position: 'absolute', 
            right: -6, 
            top: expanded ? -4 : '50%', 
            width: 28, 
            height: 28, 
            justifyContent: 'center', 
            alignItems: 'center', 
            transform: expanded ? [] : [{ translateY: -14 }] 
          }}
        >
          <PlatformIcon 
            name={expanded ? "expand_less" : "expand_more"} 
            size={20} 
            color="#6B7280" 
            family="MaterialIcons"
          />
        </TouchableOpacity>
      </View>
    </View>
  );
};

export default OpeningHoursRow;
